import React, { useMemo, useState } from 'react'
import { Sparkles } from 'lucide-react'

export default function DynamicPrompts({ onSelectPrompt, contextData, gameData, compact = false, hasMessages = false }) {
  // Hide suggested queries entirely
  return null
  
  const [clickedIdx, setClickedIdx] = useState(null)
  // Color coding by intent
  const intentColors = {
    analysis: { bg: 'rgba(255, 77, 109, 0.08)', text: 'var(--accent-1)' },      // Coral - Live analysis
    comparison: { bg: 'rgba(255, 158, 0, 0.08)', text: 'var(--accent-2)' },    // Amber - Comparisons
    prediction: { bg: 'rgba(166, 77, 255, 0.08)', text: 'var(--accent-3)' },   // Violet - Predictions
    insight: { bg: 'rgba(48, 230, 230, 0.08)', text: '#30E6E6' }               // Cyan - Insights
  }

  // Get current hour to determine time-appropriate prompts
  const currentHour = new Date().getHours()
  const isGameTime = currentHour >= 19 && currentHour <= 23 // 7pm-11pm typical game time
  const isPreGame = currentHour >= 12 && currentHour < 19 // Noon-7pm
  const isPostGame = currentHour >= 0 && currentHour < 12 // Midnight-Noon (next day)
  
  // Check day of week for NFL (Thu/Sun/Mon)
  const dayOfWeek = new Date().getDay()
  const isNFLDay = dayOfWeek === 0 || dayOfWeek === 1 || dayOfWeek === 4 // Sun, Mon, Thu

  // Sport-specific prompts for the split view (starter only)
  const sportPrompts = useMemo(() => {
    // NBA prompts (left side)
    const nbaPrompts = isGameTime ? [
      { title: "NBA scores right now", sport: 'nba' },
      { title: "Any close NBA games?", sport: 'nba' },
    ] : isPreGame ? [
      { title: "NBA games tonight", sport: 'nba' },
      { title: "NBA injury report", sport: 'nba' },
    ] : [
      { title: "NBA recap last night", sport: 'nba' },
      { title: "NBA standings", sport: 'nba' },
    ]
    
    // NFL prompts (right side)
    const nflPrompts = isNFLDay ? (isGameTime ? [
      { title: "NFL scores right now", sport: 'nfl' },
      { title: "Any NFL upsets?", sport: 'nfl' },
    ] : isPreGame ? [
      { title: "NFL games today", sport: 'nfl' },
      { title: "NFL injury report", sport: 'nfl' },
    ] : [
      { title: "NFL recap", sport: 'nfl' },
      { title: "NFL standings", sport: 'nfl' },
    ]) : [
      { title: "NFL this week", sport: 'nfl' },
      { title: "NFL playoff picture", sport: 'nfl' },
    ]
    
    return { nba: nbaPrompts, nfl: nflPrompts }
  }, [isGameTime, isPreGame, isNFLDay])

  const prompts = useMemo(() => {
    // STARTER PROMPTS - return null to use split view instead
    if (!contextData) {
      return null // Signal to use split sport view
    }
    
    // Smart prompts based on context type
    const isPlayer = contextData.title?.includes('Stats')
    const isGame = gameData?.teams?.length > 0
    
    if (isPlayer) {
      return [
        { 
          title: `${contextData.title.replace(' Stats', '')} vs league average`, 
          icon: '📊', 
          desc: 'Performance ranking',
          intent: 'comparison'
        },
        { 
          title: `Why ${contextData.title.replace(' Stats', '')} is key today`, 
          icon: '🔴', 
          desc: 'Game impact analysis',
          intent: 'analysis'
        },
        { 
          title: `${contextData.title.replace(' Stats', '')} career trajectory`, 
          icon: '📈', 
          desc: 'Trend analysis',
          intent: 'insight'
        },
        { 
          title: `Betting props on ${contextData.title.replace(' Stats', '')}`, 
          icon: '💰', 
          desc: 'Vegas opportunities',
          intent: 'prediction'
        }
      ]
    }
    
    if (isGame) {
      const team1 = gameData?.teams?.[0]?.name || 'Team A'
      const team2 = gameData?.teams?.[1]?.name || 'Team B'
      return [
        { 
          title: `${team1} vs ${team2} head-to-head`, 
          icon: '⚡', 
          desc: 'Historical matchup data',
          intent: 'comparison'
        },
        { 
          title: `Live play-by-play breakdown`, 
          icon: '🔴', 
          desc: 'What\'s happening now',
          intent: 'analysis'
        },
        { 
          title: `Game log`, 
          icon: '📊', 
          desc: 'Last 5 games stats',
          intent: 'insight'
        },
        { 
          title: `Key player matchups to watch`, 
          icon: '👥', 
          desc: 'Strategic insights',
          intent: 'insight'
        }
      ]
    }
    
    // Fallback
    return [
      { 
        title: `Deep dive: ${contextData.title}`, 
        icon: '📊', 
        desc: 'Full analysis',
        intent: 'analysis'
      },
      { 
        title: `Compare ${contextData.title}`, 
        icon: '🔄', 
        desc: 'vs others',
        intent: 'comparison'
      },
      { 
        title: `Predict ${contextData.title}`, 
        icon: '🎯', 
        desc: 'Next outcome',
        intent: 'prediction'
      },
      { 
        title: `Insights on ${contextData.title}`, 
        icon: '💡', 
        desc: 'Hidden patterns',
        intent: 'insight'
      }
    ]
  }, [contextData, gameData, isGameTime, isPreGame, isPostGame])

  // Side-by-side starter prompts (NBA left, NFL right) - matches input area style
  if (prompts === null) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center',
        alignItems: 'center',
        gap: '4px', 
        paddingBottom: '4px',
        flexWrap: 'wrap',
        maxWidth: '100%',
        overflow: 'hidden',
      }}>
        {/* NBA prompts */}
        {sportPrompts.nba.map((prompt, idx) => (
          <button
            key={`nba-${idx}`}
            onClick={() => onSelectPrompt(prompt.title, 'starter')}
            className="transition-all duration-150 active:scale-[0.97]"
            style={{
              padding: '5px 10px',
              backgroundColor: 'transparent',
              borderRadius: '14px',
              fontSize: '11px',
              fontWeight: '400',
              color: '#6B7280',
              whiteSpace: 'nowrap',
              border: '1px solid #E5E7EB',
              cursor: 'pointer',
              boxShadow: 'none',
              transition: 'all 0.15s ease',
              flexShrink: 1,
              minWidth: 0,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.08)'
              e.currentTarget.style.borderColor = '#D1D5DB'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = 'none'
              e.currentTarget.style.borderColor = '#E5E7EB'
            }}
          >
            {prompt.title}
          </button>
        ))}
        
        {/* Subtle divider - hidden on mobile */}
        <div className="hidden sm:block" style={{ width: '1px', height: '14px', backgroundColor: '#E5E7EB', margin: '0 1px' }} />
        
        {/* NFL prompts */}
        {sportPrompts.nfl.map((prompt, idx) => (
          <button
            key={`nfl-${idx}`}
            onClick={() => onSelectPrompt(prompt.title, 'starter')}
            className="transition-all duration-150 active:scale-[0.97]"
            style={{
              padding: '5px 10px',
              backgroundColor: 'transparent',
              borderRadius: '14px',
              fontSize: '11px',
              fontWeight: '400',
              color: '#6B7280',
              whiteSpace: 'nowrap',
              border: '1px solid #E5E7EB',
              cursor: 'pointer',
              boxShadow: 'none',
              transition: 'all 0.15s ease',
              flexShrink: 1,
              minWidth: 0,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.08)'
              e.currentTarget.style.borderColor = '#D1D5DB'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = 'none'
              e.currentTarget.style.borderColor = '#E5E7EB'
            }}
          >
            {prompt.title}
          </button>
        ))}
      </div>
    )
  }

  // Clean minimal chips - centered (for follow-up prompts)
  return (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'center',
      flexWrap: 'wrap',
      gap: '6px', 
      paddingBottom: '2px',
    }}>
      {prompts.map((prompt, idx) => (
        <button
          key={idx}
          onClick={() => onSelectPrompt(prompt.title, 'starter')}
          className="flex-shrink-0 transition-all duration-150 hover:bg-gray-100 active:scale-[0.97]"
          style={{
            padding: '6px 12px',
            backgroundColor: 'transparent',
            borderRadius: '16px',
            fontSize: '12px',
            fontWeight: '450',
            color: '#6B7280',
            whiteSpace: 'nowrap',
            border: '1px solid #E5E7EB',
            cursor: 'pointer',
          }}
        >
          {prompt.title}
        </button>
      ))}
    </div>
  )
}
